import React, { useState, useEffect } from 'react';
import { 
  FiUpload, 
  FiPlay, 
  FiDownload, 
  FiEye, 
  FiFileText, 
  FiUsers, 
  FiActivity,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiRefreshCw,
  FiCopy
} from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api/";

const CommentGenerationPage = () => {
  const { token, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingSessionId, setGeneratingSessionId] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('sessions');
  const [selectedSession, setSelectedSession] = useState(null);
  const [comments, setComments] = useState({});
  const [pollingInterval, setPollingInterval] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  // Fetch sessions and templates on component mount
  useEffect(() => {
    fetchSessions();
    fetchTemplates();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}comment-generations/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setSessions(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError('Failed to fetch sessions');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}comment-templates/`, {
        headers: { Authorization: `Token ${token}` }
      });
      setTemplates(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to fetch templates');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedTemplate || !sessionName) {
      setError('Please provide session name, select a template, and choose a file');
      return;
    }

    setLoading(true);
    setError('');
    setProcessingMessage('Uploading file and processing student data...');

    const formData = new FormData();
    formData.append('session_name', sessionName);
    formData.append('excel_file', selectedFile);
    formData.append('template_id', selectedTemplate);

    try {
      const response = await axios.post(`${API_URL}comment-generations/upload_excel/`, formData, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setProcessingMessage(`File processed successfully! Found ${response.data.students_found || 0} students.`);
        
        setSessionName('');
        setSelectedFile(null);
        setSelectedTemplate('');
        fetchSessions(); // This will refresh the sessions list with the new status
        setActiveTab('sessions');
        
        // Clear processing message after 3 seconds
        setTimeout(() => {
          setProcessingMessage('');
        }, 3000);
      } else {
        setError(response.data.error || 'Upload failed');
        setProcessingMessage('');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.response?.data?.error || 'Upload failed');
      setProcessingMessage('');
    } finally {
      setLoading(false);
    }
  };

  const generateComments = async (sessionId) => {
    try {
      setGeneratingSessionId(sessionId);
      setProcessingMessage('Starting AI comment generation...');
      setError('');
      
      const response = await axios.post(`${API_URL}comment-generations/${sessionId}/generate_comments/`, {}, {
        headers: { Authorization: `Token ${token}` }
      });

      if (response.data.success) {
        setProcessingMessage('AI is generating personalized comments for each student...');
        // Start polling for status updates
        startPolling(sessionId);
      } else {
        setError(response.data.error || 'Failed to start comment generation');
        setGeneratingSessionId(null);
        setProcessingMessage('');
      }
    } catch (error) {
      console.error('Error generating comments:', error);
      setError(error.response?.data?.error || 'Failed to generate comments');
      setGeneratingSessionId(null);
      setProcessingMessage('');
    }
  };

  const startPolling = (sessionId) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}comment-generations/${sessionId}/`, {
          headers: { Authorization: `Token ${token}` }
        });

        const session = response.data;
        setSessions(prev => prev.map(s => s.id === sessionId ? session : s));

        // Update processing message based on status
        if (session.status === 'generating') {
          setProcessingMessage('AI is generating personalized comments for each student...');
        } else if (session.status === 'completed') {
          setProcessingMessage('Comment generation completed successfully!');
          setTimeout(() => {
            setGeneratingSessionId(null);
            setProcessingMessage('');
          }, 3000);
        } else if (session.status === 'failed') {
          setProcessingMessage('');
          setError('Comment generation failed. Please try again.');
          setGeneratingSessionId(null);
        }

        if (session.status === 'completed' || session.status === 'failed') {
          clearInterval(interval);
          setPollingInterval(null);
        }
      } catch (error) {
        console.error('Error polling session status:', error);
        clearInterval(interval);
        setPollingInterval(null);
      }
    }, 2000);

    setPollingInterval(interval);
  };

  const previewComments = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}comment-generations/${sessionId}/preview_comments/`, {
        headers: { Authorization: `Token ${token}` }
      });

      setComments(response.data.comments || {});
      setSelectedSession(sessionId);
      setActiveTab('preview');
    } catch (error) {
      console.error('Error previewing comments:', error);
      setError(error.response?.data?.error || 'Failed to preview comments');
    }
  };

  const exportComments = async (sessionId, format) => {
    try {
      const response = await axios.post(`${API_URL}comment-generations/${sessionId}/export_comments/`, {
        format: format,
        include_student_data: true
      }, {
        headers: { Authorization: `Token ${token}` },
        responseType: 'blob'
      });

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `comments_${sessionId}.${format}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting comments:', error);
      setError(error.response?.data?.error || 'Failed to export comments');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FiCheck className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <FiX className="w-4 h-4 text-red-500" />;
      case 'processing':
      case 'generating':
        return <FiRefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'processed':
        return <FiCheck className="w-4 h-4 text-blue-500" />;
      default:
        return <FiActivity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'processing':
      case 'generating':
        return 'text-blue-600 bg-blue-50';
      case 'processed':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const copyStudentComments = async (studentName, studentComments) => {
    try {
      // Format the comments for copying
      let textToCopy = `${studentName}\n\n`;
      
      const commentSections = studentComments.comment_sections || {};
      for (const [sectionKey, section] of Object.entries(commentSections)) {
        textToCopy += `${section.title}:\n`;
        if (section.subtitle) {
          textToCopy += `${section.subtitle}\n`;
        }
        textToCopy += `${section.content}\n\n`;
      }
      
      // Copy to clipboard
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(`Copied ${studentName}'s comments!`);
      
      // Clear success message after 2 seconds
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (error) {
      console.error('Failed to copy comments:', error);
      setCopySuccess('Failed to copy comments');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const copyIndividualSection = async (studentName, sectionTitle, sectionContent) => {
    try {
      const textToCopy = `${studentName} - ${sectionTitle}:\n${sectionContent}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(`Copied ${sectionTitle} for ${studentName}!`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (error) {
      console.error('Failed to copy section:', error);
      setCopySuccess('Failed to copy section');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Comment Generation</h1>
          <p className="text-gray-600">Generate personalized report comments from Excel assessment data</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <FiAlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        )}

        {copySuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <FiCheck className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-green-700">{copySuccess}</span>
          </div>
        )}

        {processingMessage && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
            <FiRefreshCw className="w-5 h-5 text-blue-500 mr-2 animate-spin" />
            <span className="text-blue-700">{processingMessage}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiUpload className="w-4 h-4 inline mr-1" />
              Upload Excel
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sessions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FiFileText className="w-4 h-4 inline mr-1" />
              Sessions ({sessions.length})
            </button>
            {selectedSession && (
              <button
                onClick={() => setActiveTab('preview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'preview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FiEye className="w-4 h-4 inline mr-1" />
                Preview Comments
              </button>
            )}
          </nav>
        </div>

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Upload Assessment Data File</h2>
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Name
                </label>
                <input
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Grade 9 Mid-Year CAT Report"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a template...</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {template.grade_level} {template.subject}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assessment Data File
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Upload Excel or CSV file with student assessment data (.xlsx, .xls, or .csv)
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4 mr-2" />
                    Upload & Process
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Comment Generation Sessions</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <FiFileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No sessions found. Upload an Excel file to get started.</p>
                </div>
              ) : (
                sessions.map(session => (
                  <div key={session.id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{session.session_name}</h3>
                        <div className="flex items-center mt-2 space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <FiUsers className="w-4 h-4 mr-1" />
                            {session.total_students || 0} students
                          </span>
                          <span className="flex items-center">
                            <FiFileText className="w-4 h-4 mr-1" />
                            {session.template?.name || 'No template'}
                          </span>
                          <span className="flex items-center">
                            <FiActivity className="w-4 h-4 mr-1" />
                            {session.comments_generated || 0} comments generated
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                          {getStatusIcon(session.status)}
                          <span className="ml-1 capitalize">{session.status}</span>
                        </div>
                        
                        {session.status === 'processed' && (
                          <button
                            onClick={() => generateComments(session.id)}
                            disabled={generatingSessionId === session.id}
                            className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {generatingSessionId === session.id ? (
                              <>
                                <FiRefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <FiPlay className="w-4 h-4 mr-1" />
                                Generate
                              </>
                            )}
                          </button>
                        )}
                        
                        {session.status === 'completed' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => previewComments(session.id)}
                              className="bg-gray-600 text-white px-3 py-1 rounded-md text-sm hover:bg-gray-700 flex items-center"
                            >
                              <FiEye className="w-4 h-4 mr-1" />
                              Preview
                            </button>
                            <div className="relative">
                              <button
                                onClick={() => exportComments(session.id, 'excel')}
                                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 flex items-center"
                              >
                                <FiDownload className="w-4 h-4 mr-1" />
                                Excel
                              </button>
                            </div>
                            <button
                              onClick={() => exportComments(session.id, 'csv')}
                              className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 flex items-center"
                            >
                              <FiDownload className="w-4 h-4 mr-1" />
                              CSV
                            </button>
                            <button
                              onClick={() => exportComments(session.id, 'pdf')}
                              className="bg-red-600 text-white px-3 py-1 rounded-md text-sm hover:bg-red-700 flex items-center"
                            >
                              <FiDownload className="w-4 h-4 mr-1" />
                              PDF
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {session.progress_percentage !== undefined && session.status === 'generating' && (
                      <div className="mt-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${session.progress_percentage}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {session.progress_percentage}% complete
                        </p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && selectedSession && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Comment Preview</h2>
            </div>
            <div className="p-6">
              {Object.keys(comments).length === 0 ? (
                <p className="text-gray-500 text-center">No comments to preview</p>
              ) : (
                <div className="space-y-6">
                  {Object.entries(comments).map(([studentName, studentComments]) => (
                    <div key={studentName} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">{studentName}</h3>
                        <button
                          onClick={() => copyStudentComments(studentName, studentComments)}
                          className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                        >
                          <FiCopy className="w-4 h-4 mr-1" />
                          Copy All Comments
                        </button>
                      </div>
                      <div className="space-y-4">
                        {Object.entries(studentComments.comment_sections || {}).map(([sectionKey, section]) => (
                          <div key={sectionKey} className="border-l-4 border-blue-500 pl-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-medium text-gray-700">{section.title}</h4>
                                {section.subtitle && (
                                  <p className="text-sm text-gray-500 italic">{section.subtitle}</p>
                                )}
                              </div>
                              <button
                                onClick={() => copyIndividualSection(studentName, section.title, section.content)}
                                className="flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                              >
                                <FiCopy className="w-3 h-3 mr-1" />
                                Copy
                              </button>
                            </div>
                            <div className="mt-2 text-gray-600 whitespace-pre-line">
                              {section.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentGenerationPage;